/** Game constants: mode objectives, presets, quick play, match events, blackout and Last Light. Part of src/constants.ts (re-exported there; import from '../constants'). */
import type { QuickPlayPreset, GameMode } from '../types';

// =============================================================================
// STAKES / TERRITORY
// =============================================================================

export const STAKE_REMOVE_MOVEMENT_COST = 1.0;
export const STAKE_REMOVE_RANGE = 2.0;     // arc distance on planet surface
export const DEFAULT_SCAN_RANGE = 3.0;     // world-space distance for detection

// =============================================================================
// CARRYABLE OBJECTS
// =============================================================================

export const CARRYABLE_PICKUP_RANGE = 2.0;       // arc distance on planet
export const CARRYABLE_PICKUP_COST = 0;            // pickup is free
export const DEFAULT_CARRIER_SPEED_PENALTY = 0.5;  // 50% movement reduction
export const GROUND_ITEM_PICKUP_RANGE = 1.5;       // arc distance for dropped heal auto-pickup

// =============================================================================
// RESPAWN
// =============================================================================

export const DEFAULT_RESPAWN_COOLDOWN = 2;
export const DEFAULT_RESPAWN_TICKETS = 8;   // Phase 44: 15 let stalled CTF matches run to the cap
export const RESPAWN_TICKET_DRAIN_CAPTURE = 3;

// =============================================================================
// CTF
// =============================================================================

export const CTF_CAPTURES_REQUIRED = 2;   // Phase 44: 3 never resolved for bots in 30 rounds

// =============================================================================
// DEPLOY PHASE
// =============================================================================

export const DROP_CAN_APPROACH_DURATION = 3.0;  // seconds for orbital approach
export const DROP_CAN_DESCEND_DURATION = 2.0;   // seconds for descent to surface
export const DROP_CAN_STAGGER_DELAY = 1.5;      // seconds between team drops

// =============================================================================
// INTERACTIONS — structure interaction
// =============================================================================

export const INTERACT_RANGE = 2.0;
export const INTERACT_MOVEMENT_COST = 0.5;

// =============================================================================
// COLLECTIBLES
// =============================================================================

export const COLLECT_RANGE = 2.0;
export const COLLECT_MOVEMENT_COST = 0.5;
export const DEFAULT_CARRY_CAPACITY = 3;

// =============================================================================
// EXTRACTION
// =============================================================================

export const EXTRACTION_RANGE = 3.0;
export const DEFAULT_EXTRACTION_TURNS = 3;   // Phase 44

// =============================================================================
// SIGNAL TRACE
// =============================================================================

export const DEFAULT_POSTS_TO_TRIANGULATE = 2;   // Phase 44: 3 planets is beyond bot transit; 2 produces reveals
export const DEFAULT_POST_ACTIVATION_TURNS = 2;

// =============================================================================
// UPLINK
// =============================================================================

export const DEFAULT_FRAGMENTS_REQUIRED = 2;   // Phase 44: bots reach 2 distinct-planet fragments, never 3 or 4
export const DEFAULT_VAULT_ACTIVATION_TURNS = 1;
export const DEFAULT_UPLOAD_TURNS = 1;

// =============================================================================
// SALVAGE RUN
// =============================================================================

export const DEFAULT_SALVAGE_NODE_COUNT = 20;
export const SALVAGE_VALUE_RANGE: [number, number] = [5, 25];
export const SALVAGE_WEIGHT_RANGE: [number, number] = [1, 3];

// =============================================================================
// EXCAVATION
// =============================================================================

export const DEFAULT_DRILL_BUILD_TIME = 3;
export const DEFAULT_DRILL_OPERATION_TIME = 5;
export const DEFAULT_DRILL_SITE_HP = 30;

// =============================================================================
// MODE PRESETS
// =============================================================================

// =============================================================================
// QUICK-PLAY PRESETS — the setup screen's first row (see types.ts QuickPlayPreset)
// =============================================================================

const QUICK_BASE: Record<string, string | boolean> = {
  'opt-visibility': 'fog', 'opt-preview': 'full', 'opt-stabilizers': 'off', 'opt-datalinked-los': true, 'opt-bot-fog': true, 'opt-adaptive-personality': true,
  'opt-hide-enemy-deploy': false, 'opt-friendly-fire': '0', 'opt-fall-damage': '1', 'opt-turn-order': 'sequential',
  'opt-cbp': '50', 'opt-bot-personality': 'balanced', 'opt-turn-limit': '',
};

export const QUICK_PLAY_PRESETS: QuickPlayPreset[] = [
  {
    key: 'skirmish', name: 'SKIRMISH', blurb: 'You vs one bot. Six worlds around a yellow sun.',
    fields: { ...QUICK_BASE, 'opt-num-teams': '2', 'opt-bot-teams': '1', 'opt-units-per-team': '1', 'opt-bot-units': '1',
      'opt-system-preset': 'solStandard', 'opt-game-mode': 'deathmatch', 'opt-bot-difficulty': 'medium' },
  },
  {
    key: 'squad', name: 'SQUAD BATTLE', blurb: 'Three-unit teams, two bot squads, a twelve-world system.',
    fields: { ...QUICK_BASE, 'opt-num-teams': '3', 'opt-bot-teams': '2', 'opt-units-per-team': '3', 'opt-bot-units': '3',
      'opt-system-preset': 'grandOrrery', 'opt-game-mode': 'deathmatch', 'opt-bot-difficulty': 'medium' },
  },
  {
    key: 'territory', name: 'TERRITORY', blurb: 'Dominion on a tight red-dwarf cluster. Plant stakes, hold worlds. 60 turns.',
    fields: { ...QUICK_BASE, 'opt-num-teams': '3', 'opt-bot-teams': '2', 'opt-units-per-team': '2', 'opt-bot-units': '2',
      'opt-system-preset': 'emberCluster', 'opt-game-mode': 'dominion', 'opt-turn-limit': '60', 'opt-bot-difficulty': 'hard' },
  },
  {
    key: 'megaplanet', name: 'MEGAPLANET', blurb: 'Close quarters on one enormous Earth-like world. Two units each.',
    fields: { ...QUICK_BASE, 'opt-num-teams': '2', 'opt-bot-teams': '1', 'opt-units-per-team': '2', 'opt-bot-units': '2',
      'opt-system-preset': 'earth', 'opt-game-mode': 'deathmatch', 'opt-bot-difficulty': 'medium' },
  },
  { key: 'custom', name: 'CUSTOM', blurb: 'Set everything yourself under Advanced.', fields: {} },
];

export const MODE_PRESET_DESCRIPTIONS: Record<GameMode, string> = {
  deathmatch: 'Deathmatch — last team standing',
  dominion: 'Dominion — plant stakes to claim planets',
  sovereignty: 'Sovereignty — hold planets exclusively',
  ctf: 'Capture the Core — steal the enemy cores',
  signalTrace: 'Signal Trace — triangulate the hidden source',
  uplink: 'Uplink — collect and upload data fragments',
  salvageRun: 'Salvage Run — highest scrap value wins',
  excavation: 'Excavation — drill, extract, hold the artifact',
  blackout: 'Blackout — survive as planets go dark',
  lastLight: 'Last Light — the nova consumes all',
};

/** The in-fiction wording of the same descriptions (flavor text on). */
export const MODE_FLAVOR_DESCRIPTIONS: Record<GameMode, string> = {
  deathmatch: 'Standard Assessment — last contractor standing',
  dominion: 'Territory Survey — plant stakes to claim planets',
  sovereignty: 'Sovereign Claim — secure exclusive planetary control',
  ctf: 'Core Retrieval Exercise — capture enemy navigation cores',
  signalTrace: 'Signal Trace Operation — triangulate hidden signal source',
  uplink: 'Data Uplink Protocol — collect and upload data fragments',
  salvageRun: 'Salvage Recovery Mandate — highest scrap value wins',
  excavation: 'Deep Core Excavation — drill, extract, and hold the artifact',
  blackout: 'Blackout Protocol — survive as planets go dark',
  lastLight: 'Last Light Contingency — the nova consumes all',
};

export const MODE_RULES: Record<GameMode, { objective: string; rules: string[] }> = {
  deathmatch: {
    objective: 'Eliminate every enemy unit.',
    rules: [
      'Last team standing wins.',
      'No respawns — once dead, you are out.',
      'Turn limit tiebreak: most survivors, then kills, then damage dealt.',
    ],
  },
  dominion: {
    objective: 'Control the most planets when time expires.',
    rules: [
      'Press F to plant territory stakes on planets.',
      'Multiple stakes per planet allowed — all stakes are visible.',
      'Planet ownership goes to team with most stakes on it.',
      'Most owned planets at turn limit wins. Elimination also wins.',
    ],
  },
  sovereignty: {
    objective: 'Secure exclusive planetary control.',
    rules: [
      'Press F to plant a hidden stake (one per planet per team).',
      'Stakes have HP and can be destroyed by enemy fire.',
      'Nearby enemy stakes are revealed within scan range.',
      'Most controlled planets at turn limit wins.',
    ],
  },
  ctf: {
    objective: 'Capture enemy navigation cores.',
    rules: [
      'Walk onto an enemy planet\'s flag to grab their core.',
      'Return to your team\'s planet to score a capture.',
      'Carrier moves at half speed and cannot jump.',
      'First team to reach required captures wins. Respawns enabled.',
    ],
  },
  signalTrace: {
    objective: 'Triangulate and extract the hidden signal.',
    rules: [
      'Build listening posts (N) near ruins to receive clue readouts.',
      'Use clue data to triangulate the signal source location.',
      'Walk to the signal site and extract it to win.',
      'Respawns enabled with limited tickets.',
    ],
  },
  uplink: {
    objective: 'Upload data fragments to the antenna.',
    rules: [
      'Collect data fragments from vaults at ruins across planets.',
      'Carry fragments to the antenna ruin and upload them.',
      'First team to upload the required number of unique fragments wins.',
      'Carrier has speed penalty. Respawns enabled.',
    ],
  },
  salvageRun: {
    objective: 'Deliver the highest total salvage value.',
    rules: [
      'Collect salvage from scattered nodes across the system.',
      'Deliver salvage to your team\'s depot planet for points.',
      'Each unit can carry up to 3 salvage pieces.',
      'Short game — no respawns. Highest value at turn limit wins.',
    ],
  },
  excavation: {
    objective: 'Extract and hold the deep core artifact.',
    rules: [
      'Build a drill (N) at the artifact site — takes several turns.',
      'Protect the drill while it operates.',
      'Once extracted, the artifact becomes carriable.',
      'Team holding the artifact wins. Respawns enabled.',
    ],
  },
  blackout: {
    objective: 'Survive as planets go dark one by one.',
    rules: [
      'Each turn, one or more planets lose all light.',
      'Dark planets drastically reduce visibility — only nearby units can see.',
      'Warnings are issued before a planet goes dark.',
      'Last team standing wins. Deathmatch elimination rules.',
    ],
  },
  lastLight: {
    objective: 'Survive the nova as planets are destroyed from the inside out.',
    rules: [
      'The star is going nova — planets are destroyed on a schedule.',
      'Innermost planets are destroyed first.',
      'Warnings are given before destruction.',
      'Units on destroyed planets die. Last team standing wins.',
    ],
  },
};

export const MODE_DISPLAY_NAMES: Record<GameMode, string> = {
  deathmatch: 'Team Deathmatch',
  dominion: 'Dominion',
  sovereignty: 'Sovereignty',
  ctf: 'Capture the Core',
  signalTrace: 'Signal Trace',
  uplink: 'Uplink',
  salvageRun: 'Salvage Run',
  excavation: 'Excavation',
  blackout: 'Blackout',
  lastLight: 'Last Light',
};

// =============================================================================
// MATCH EVENTS
// =============================================================================

export const EVENT_MIN_TURN = 5;
export const EVENT_INTERVAL_RARE = 20;
export const EVENT_INTERVAL_NORMAL = 12;
export const EVENT_INTERVAL_FREQUENT = 6;
export const SOLAR_FLARE_DURATION = 2;
export const ASTEROID_PASS_DURATION = 3;
export const COMMS_BLACKOUT_DURATION = 2;
export const PPC_INSPECTION_CBP_BONUS = 5;
export const BOUNTY_SURVEY_CBP = 3;
/** R14 (c): turns a survey bounty stays open for a probe to fly over its target. */
export const BOUNTY_SURVEY_TURNS = 3;
export const BOUNTY_PRESERVE_CBP = 3;
export const ASTEROID_MASS = 50;

// =============================================================================
// BLACKOUT MODE
// =============================================================================

export const DEFAULT_BLACKOUT_RATE = 1;
export const DEFAULT_BLACKOUT_DELAY = 1;
export const DEFAULT_BLACKOUT_START_TURN = 5;
export const DEFAULT_DARK_VISIBILITY = 2.0;

// =============================================================================
// LAST LIGHT MODE
// =============================================================================

// =============================================================================
// LAST LIGHT MODE
// =============================================================================

export const DEFAULT_DESTRUCTION_INTERVAL = 5;
export const DEFAULT_WARNING_TURNS = 2;
export const DEFAULT_FINAL_PLANETS_COUNT = 1;
